'use client';

import { filter, isEmpty } from 'lodash';
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useMemo,
  useState,
} from 'react';

import { createCustomContext } from './context';

export interface TInpaintFile {
  fileUrl: string;
  file?: File | null;
  mask?: HTMLCanvasElement | null;
  id: string;
}

export enum ETool {
  RUBBER = 'rubber',
  BRUSH = 'brush',
  NONE = 'none',
}

interface Props {
  brushSize: number;
  setBrushSize: Dispatch<SetStateAction<number>>;
  currentTool: ETool;
  setTool: Dispatch<SetStateAction<ETool>>;
  isUndoDraw: boolean;
  setUndoDraw: Dispatch<SetStateAction<boolean>>;
  isCleanDraw: boolean;
  setCleanDraw: Dispatch<SetStateAction<boolean>>;
  isMaskLoading: boolean;
  startLoading: (id: string) => void;
  stopLoading: (id: string) => void;
  inpaintFile: TInpaintFile;
  setInpaintFile: Dispatch<SetStateAction<TInpaintFile>>;
  hasMask: boolean;
  setHasMask: Dispatch<SetStateAction<boolean>>;
}

const [Context, useAutoMaskContext] = createCustomContext<Props>({
  name: 'AutoMaskContext',
});
export { useAutoMaskContext };

export const AutoMaskProvider: FC<PropsWithChildren> = ({ children }) => {
  const [inpaintFile, setInpaintFile] = useState<TInpaintFile>({
    fileUrl: '',
    file: null,
    mask: null,
    id: '',
  });

  const [hasMask, setHasMask] = useState(false);

  // Состояние для размера кисти
  const [brushSize, setBrushSize] = useState(10);

  // Состояние для текущего инструмента рисования
  const [currentTool, setTool] = useState<ETool>(ETool.BRUSH);

  // Состояние для управления отменой действия
  const [isUndoDraw, setUndoDraw] = useState(false);

  // Состояние для управления очисткой маски
  const [isCleanDraw, setCleanDraw] = useState(false);

  // Состояние "загрузки" для маультимасок
  const [loadingIds, setLoadingIds] = useState<string[]>([]);

  const isMaskLoading = useMemo(() => !isEmpty(loadingIds), [loadingIds]);

  const startLoading = (id: string) => {
    setLoadingIds((prev) => [...prev, id]);
  };
  const stopLoading = (id: string) => {
    setLoadingIds((prev) => filter(prev, (loadingId) => loadingId !== id));
  };

  return (
    <Context
      value={{
        currentTool,
        setTool,
        brushSize,
        setBrushSize,
        isUndoDraw,
        setUndoDraw,
        isCleanDraw,
        setCleanDraw,
        isMaskLoading,
        startLoading,
        stopLoading,
        inpaintFile,
        setInpaintFile,
        hasMask,
        setHasMask,
      }}
    >
      {children}
    </Context>
  );
};
